<template>
  <MarketplaceCard
    v-model="switchOn"
    :disabled="switchDisabled"
    description="RingCentral is a cloud-based VOIP solution that enables call tracking and customer communication insights."
  >
    <template #logo>
      <div class="w-full d-flex align-center h-12">
        <RingCentralLogo class="h-8 my-auto" />
      </div>
    </template>
    <template #configure>
      <div>
        <div class="link" @click="openModal">Set Up Information</div>
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpened"
        :saving="isSaving"
        :deleting="isDeleting"
        title="Set Up Information - Ring Central"
        @submit="saveIntegration"
        @delete="deleteIntegration"
        @hide-modal="closeModal"
      >
        <div class="flex flex-col w-full">
          <TextField
            v-model="ringCentral.api_key"
            name="API Key"
            placeholder="API Key"
            class="mb-2"
          />
        </div>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import RingCentralLogo from "@/images/ring_central_logo.svg?component";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const modalOpened = ref(false);
const switchDisabled = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const switchOn = ref(null);

const ringCentral = computed(() => store.state.ringCentral.config);

watch(
  () => ringCentral.value.enabled,
  (newVal, oldVal) => {
    if (newVal !== oldVal) refreshTools();
  }
);

// Disable the toggle if the integration gets deleted
watch(
  () => ringCentral.value.persisted,
  (newVal) => {
    if (!newVal) switchDisabled.value = true;
  }
);

watch(switchOn, (newValue, oldValue) => {
  if (oldValue === null || newValue === oldValue) return;
  if (!ringCentral.value.persisted) return;
  newValue ? enableIntegration() : disableIntegration();
});

onMounted(async () => {
  await store.dispatch("fetchRingCentral");
  refreshTools();
});

const openModal = () => {
  modalOpened.value = true;
};

const closeModal = async () => {
  modalOpened.value = false;
  await store.dispatch("fetchRingCentral");
  refreshTools();
};

const refreshTools = () => {
  switchDisabled.value = !ringCentral.value.persisted;
  switchOn.value = ringCentral.value.enabled;
};

const enableIntegration = async () => {
  await store.dispatch("changeRingCentral", { enabled: true });
  window.notify.success("Ring Central integration enabled!");
};

const disableIntegration = async () => {
  await store.dispatch("changeRingCentral", { enabled: false });
  window.notify.success("Ring Central integration disabled!");
};

const saveIntegration = async () => {
  isSaving.value = true;
  await store.dispatch("changeRingCentral", {
    api_key: ringCentral.value.api_key,
    enabled: ringCentral.value.enabled
  });
  isSaving.value = false;
  closeModal();
};

const deleteIntegration = async () => {
  isDeleting.value = true;
  switchOn.value = false;
  if (ringCentral.value.persisted) {
    await store.dispatch("dropRingCentral");
    refreshTools();
    window.notify.success("RingCentral settings deleted!");
  } else {
    window.notify.error(
      "The integration has not been configured yet. There's nothing to delete"
    );
  }
  isDeleting.value = false;
  closeModal();
};
</script>
